import { useId, useRef, type ReactNode } from 'react';
import './ui.css';

/**
 * Formulierbouwstenen. Elk veld heeft een gekoppeld label, een optionele hint
 * en foutmelding, en raakvlakken van minimaal 44px voor gebruik buiten.
 */

interface VeldProps {
  label: string;
  /** Mag opmaak bevatten; de schrijftip is een blokje met voorbeelden. */
  hint?: ReactNode;
  fout?: string;
  verplicht?: boolean;
  /** Wie dit veld te lezen krijgt; verschijnt als chip naast het label. */
  lezers?: Lezers;
  children: (id: string) => ReactNode;
}

/**
 * Wie dit veld te lezen krijgt.
 *
 * Dit stond nergens, en dat was het echte probleem. De ene toelichting gaat
 * mee naar de gemeente en de andere blijft bij de coaches, en niets op het
 * scherm verried welke. Een regel als "schrijf hier voorzichtiger" kun je
 * niet toepassen als je niet ziet waar je bent.
 */
export type Lezers = 'team' | 'gemeente';

const LEZERS_TEKST: Record<Lezers, string> = {
  team: 'alleen intern',
  gemeente: 'gemeente ziet dit',
};

export function Veld({
  label,
  hint,
  fout,
  verplicht,
  lezers,
  children,
}: VeldProps): JSX.Element {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const foutId = fout ? `${id}-fout` : undefined;

  return (
    <div className="veld">
      <label className="veld__label" htmlFor={id}>
        {label}
        {verplicht && (
          <span className="veld__verplicht" aria-hidden="true">
            *
          </span>
        )}
        {lezers && <span className={`veld__lezers veld__lezers--${lezers}`}>{LEZERS_TEKST[lezers]}</span>}
      </label>
      {children(id)}
      {hint && (
        <span className="veld__hint" id={hintId}>
          {hint}
        </span>
      )}
      {fout && (
        <span className="veld__fout" id={foutId} role="alert">
          {fout}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tekst / getal / datum                                               */
/* ------------------------------------------------------------------ */

interface TekstveldProps {
  label: string;
  waarde: string;
  bijWijziging: (waarde: string) => void;
  type?: 'text' | 'tel' | 'email' | 'date' | 'search';
  placeholder?: string;
  hint?: string;
  fout?: string;
  verplicht?: boolean;
  autoComplete?: string;
  readOnly?: boolean;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'search';
}

export function Tekstveld({
  label,
  waarde,
  bijWijziging,
  type = 'text',
  placeholder,
  hint,
  fout,
  verplicht,
  autoComplete,
  readOnly,
  inputMode,
}: TekstveldProps): JSX.Element {
  return (
    <Veld label={label} hint={hint} fout={fout} verplicht={verplicht}>
      {(id) => (
        <input
          id={id}
          className={`invoer${fout ? ' invoer--fout' : ''}`}
          type={type}
          value={waarde}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          readOnly={readOnly}
          onChange={(event) => bijWijziging(event.target.value)}
        />
      )}
    </Veld>
  );
}

interface GetalveldProps {
  label: string;
  waarde: number | null;
  bijWijziging: (waarde: number | null) => void;
  min?: number;
  max?: number;
  /** Hoeveel de knoppen erbij of eraf doen. */
  stap?: number;
  /** Wordt achter het getal gezet, bijvoorbeeld m³. */
  eenheid?: string;
  hint?: string;
  fout?: string;
}

/**
 * Getal met een min- en een plusknop.
 *
 * Aan de deur is een toetsenbord het probleem: het pakt het halve scherm en
 * haalt de coach uit zijn gesprek. Twee knoppen van 56 px doen hetzelfde werk
 * zonder dat er iets opengaat. Typen kan nog wel — het veld ertussen blijft
 * een invoerveld, voor wie precies 3,7 wil invullen.
 */
export function Getalveld({
  label,
  waarde,
  bijWijziging,
  min = 0,
  max,
  stap = 0.25,
  eenheid,
  hint,
  fout,
}: GetalveldProps): JSX.Element {
  const huidig = waarde ?? 0;

  /*
   * De laatst gezette waarde, los van de prop.
   *
   * React bundelt tikken die vlak na elkaar komen. Zonder dit zou elke tik
   * rekenen op de waarde van vóór de vorige, en dan levert drie keer plus
   * gewoon 0,25 op in plaats van 0,75 — precies wat er misgaat als je snel
   * naar 2 kuub wilt.
   */
  const laatste = useRef<number | null>(waarde);
  laatste.current = waarde;

  const stapMet = (richting: 1 | -1) => {
    const basis = laatste.current ?? 0;
    // Op twee decimalen afronden: met stappen van 0,25 loopt een halve kuub
    // anders vast op 0,7499999999.
    const nieuw = Math.round((basis + richting * stap) * 100) / 100;
    if (nieuw < min) return;
    if (max !== undefined && nieuw > max) return;
    // Terug naar leeg als je op nul uitkomt en er nog niets stond: dan blijft
    // het verschil zichtbaar tussen "nul kuub" en "nog niet ingevuld".
    const resultaat = nieuw === 0 && laatste.current === null ? null : nieuw;
    laatste.current = resultaat;
    bijWijziging(resultaat);
  };

  return (
    <Veld label={label} hint={hint} fout={fout}>
      {(id) => (
        <div className="stepper">
          <button
            type="button"
            className="stepper__knop"
            onClick={() => stapMet(-1)}
            disabled={huidig <= min && waarde !== null}
            aria-label={`${label} verlagen`}
          >
            &minus;
          </button>

          <div className={`stepper__waarde${fout ? ' invoer--fout' : ''}`}>
            {/* Dit blijft een gewoon invoerveld: de knoppen zijn er voor het
                snelle werk, maar 1,15 tik je gewoon in. */}
            <input
              id={id}
              type="number"
              inputMode="decimal"
              step="any"
              min={min}
              max={max}
              value={waarde ?? ''}
              placeholder="0"
              onFocus={(event) => event.target.select()}
              onChange={(event) => {
                const tekst = event.target.value;
                bijWijziging(tekst === '' ? null : Number(tekst));
              }}
            />
            {eenheid && <span className="stepper__eenheid">{eenheid}</span>}
          </div>

          <button
            type="button"
            className="stepper__knop"
            onClick={() => stapMet(1)}
            disabled={max !== undefined && huidig >= max}
            aria-label={`${label} verhogen`}
          >
            +
          </button>
        </div>
      )}
    </Veld>
  );
}

/* ------------------------------------------------------------------ */
/* Keuzelijst                                                          */
/* ------------------------------------------------------------------ */

interface KeuzeveldProps<T extends string> {
  label: string;
  waarde: T | '';
  opties: readonly T[];
  bijWijziging: (waarde: T | '') => void;
  legeOptie?: string;
  hint?: string;
  fout?: string;
  verplicht?: boolean;
  uitgeschakeld?: boolean;
}

export function Keuzeveld<T extends string>({
  label,
  waarde,
  opties,
  bijWijziging,
  legeOptie = 'Kies...',
  hint,
  fout,
  verplicht,
  uitgeschakeld,
}: KeuzeveldProps<T>): JSX.Element {
  return (
    <Veld label={label} hint={hint} fout={fout} verplicht={verplicht}>
      {(id) => (
        <select
          id={id}
          className={`keuze${fout ? ' keuze--fout' : ''}`}
          value={waarde}
          disabled={uitgeschakeld}
          onChange={(event) => bijWijziging(event.target.value as T | '')}
        >
          <option value="">{legeOptie}</option>
          {opties.map((optie) => (
            <option key={optie} value={optie}>
              {optie}
            </option>
          ))}
        </select>
      )}
    </Veld>
  );
}

/* ------------------------------------------------------------------ */
/* Meerkeuze (vinkjes)                                                 */
/* ------------------------------------------------------------------ */

interface MeerkeuzeveldProps<T extends string> {
  label: string;
  waarden: T[];
  opties: readonly T[];
  /**
   * Krijgt een functie mee in plaats van een waarde. Twee tikken vlak na
   * elkaar worden door React gebundeld; met een vaste waarde zou de tweede
   * de eerste overschrijven en zou het eerste vinkje stil verdwijnen.
   */
  bijWijziging: (bijwerken: (huidig: T[]) => T[]) => void;
  hint?: string;
}

/**
 * Meerdere opties tegelijk aanvinken. Bewust vinkjes en geen dropdown:
 * een coach ziet zo in één oogopslag wat er al gekozen is.
 */
export function Meerkeuzeveld<T extends string>({
  label,
  waarden,
  opties,
  bijWijziging,
  hint,
}: MeerkeuzeveldProps<T>): JSX.Element {
  const schakel = (optie: T) => {
    bijWijziging((huidig) =>
      huidig.includes(optie) ? huidig.filter((w) => w !== optie) : [...huidig, optie]
    );
  };

  return (
    <div className="veld">
      <span className="veld__label">{label}</span>
      <div className="meerkeuze" role="group" aria-label={label}>
        {opties.map((optie) => {
          const aan = waarden.includes(optie);
          return (
            <button
              key={optie}
              type="button"
              className={`meerkeuze__optie${aan ? ' meerkeuze__optie--aan' : ''}`}
              aria-pressed={aan}
              onClick={() => schakel(optie)}
            >
              <span className="meerkeuze__vink" aria-hidden="true">
                {aan ? '✓' : ''}
              </span>
              {optie}
            </button>
          );
        })}
      </div>
      {hint && <span className="veld__hint">{hint}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tekstvak                                                            */
/* ------------------------------------------------------------------ */

interface TekstvakProps {
  label: string;
  waarde: string;
  bijWijziging: (waarde: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  rijen?: number;
  /** Wie dit veld te lezen krijgt; verschijnt naast het label. */
  lezers?: Lezers;
}

export function Tekstvak({
  label,
  waarde,
  bijWijziging,
  placeholder,
  hint,
  rijen = 3,
  lezers,
}: TekstvakProps): JSX.Element {
  return (
    <Veld label={label} hint={hint} lezers={lezers}>
      {(id) => (
        <textarea
          id={id}
          className="tekstvak"
          rows={rijen}
          value={waarde}
          placeholder={placeholder}
          onChange={(event) => bijWijziging(event.target.value)}
        />
      )}
    </Veld>
  );
}

/* ------------------------------------------------------------------ */
/* Vinkje                                                              */
/* ------------------------------------------------------------------ */

interface VinkjeProps {
  label: string;
  aangevinkt: boolean;
  bijWijziging: (aangevinkt: boolean) => void;
  hint?: string;
}

export function Vinkje({ label, aangevinkt, bijWijziging, hint }: VinkjeProps): JSX.Element {
  return (
    <label className="vinkje">
      <input
        type="checkbox"
        checked={aangevinkt}
        onChange={(event) => bijWijziging(event.target.checked)}
      />
      <span className="vinkje__tekst">
        {label}
        {hint && <span className="vinkje__hint">{hint}</span>}
      </span>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Ja/nee-schakelaar                                                   */
/* ------------------------------------------------------------------ */

interface JaNeeProps {
  label: string;
  waarde: boolean;
  bijWijziging: (waarde: boolean) => void;
  hint?: string;
}

export function JaNee({ label, waarde, bijWijziging, hint }: JaNeeProps): JSX.Element {
  return (
    <div className="veld">
      <span className="veld__label">{label}</span>
      <div className="segment" role="group" aria-label={label}>
        <button
          type="button"
          className={`segment__optie${waarde ? ' segment__optie--actief' : ''}`}
          aria-pressed={waarde}
          onClick={() => bijWijziging(true)}
        >
          Ja
        </button>
        <button
          type="button"
          className={`segment__optie${!waarde ? ' segment__optie--actief' : ''}`}
          aria-pressed={!waarde}
          onClick={() => bijWijziging(false)}
        >
          Nee
        </button>
      </div>
      {hint && <span className="veld__hint">{hint}</span>}
    </div>
  );
}
